import React, { useCallback } from 'react';
import { SyncState } from '../../../api/studykit';
import { Icon } from '@dls/react-icon';
import { ColorDLS } from '../../../styling/color';
import { Tooltip, Typography } from '@dls/react-core';
import { IconButton, ListItemText } from '@material-ui/core';
import { MovieControlPlay, RedoAlt, Wheel } from './assets';

export const SyncStateListItem: React.FC<{
  state: SyncState;
  isLast: boolean;
  onClick: (configId: string) => void;
}> = ({ state, isLast, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(state.configId);
  }, [state, onClick]);

  const isRunning = state.state === 'running';

  const getIconForState = (state: SyncState['state']) => {
    switch (state) {
      case 'failed':
        return <Icon name={'CrossCircle'} size={'xl'} color={ColorDLS.SignalRed45} />;
      case 'success':
        return <Icon name={'CheckmarkCircle'} size={'xl'} color={ColorDLS.Green45} />;
      case 'running':
        return <Wheel spinning={true} />;
      case 'queued':
        return <Icon name={'Clock'} size={'m'} />;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '16px',
        width: 360,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', columnGap: 16, width: '100%' }}>
        <Tooltip title={state.state}>
          <div style={{ margin: -6 }}>{getIconForState(state.state)}</div>
        </Tooltip>
        <ListItemText
          style={{ flexGrow: 1 }}
          primary={
            <Typography
              variant={'h6'}
            >{`${state.algorithm.name} ${state.algorithm.version}`}</Typography>
          }
          secondary={
            <Tooltip
              arrow
              title={state.syncedTill?.toLocaleString() || ''}
              hidden={!state.syncedTill}
              placement={'bottom-start'}
            >
              <Typography variant={'body2'} color={'secondary'} style={{ marginBottom: 0 }}>
                {state.syncedTill
                  ? `Synced till ${getRelativeTime(state.syncedTill)}`
                  : 'Never ran'}
              </Typography>
            </Tooltip>
          }
        />
        <Tooltip
          arrow
          enterDelay={500}
          title={
            isRunning
              ? 'Please wait until the configuration is done running'
              : 'Trigger this configuration to run'
          }
        >
          <IconButton disabled={isRunning} onClick={handleClick}>
            {state.state === 'failed' ? <RedoAlt /> : <MovieControlPlay />}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

const units: Partial<Record<Intl.RelativeTimeFormatUnit, number>> = {
  year: 24 * 60 * 60 * 1000 * 365,
  month: (24 * 60 * 60 * 1000 * 365) / 12,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
};
const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function getRelativeTime(d1: Date, d2: Date = new Date()) {
  const elapsed = d1.getTime() - d2.getTime();

  for (const [unit, ms] of Object.entries(units)) {
    if (Math.abs(elapsed) > ms || unit === 'second')
      return rtf.format(Math.round(elapsed / ms), unit as Intl.RelativeTimeFormatUnit);
  }
}
